import { useEffect, useState } from "react";
import { Button, Modal } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useRegisterSW } from "virtual:pwa-register/react";
import { useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import "./PWAInstallPrompt.scss";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const PWAInstallPrompt = () => {
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const location = useLocation();

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(registration: ServiceWorkerRegistration | undefined) {
      Sentry.addBreadcrumb({
        category: "pwa",
        message: "Service worker registered",
        level: "info",
        data: { scope: registration?.scope },
      });
    },
    onRegisterError(error: Error) {
      Sentry.captureException(error, {
        level: "warning",
        tags: { feature: "pwa_registration" },
      });
    },
  });

  // Check if user is on authenticated route (not login page)
  const isAuthenticatedRoute = location.pathname !== "/login";

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Only show prompt if on authenticated route
      if (isAuthenticatedRoute) {
        setShowInstallPrompt(true);
      }
    };

    window.addEventListener("beforeinstallprompt", handler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, [isAuthenticatedRoute]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      Sentry.addBreadcrumb({
        category: "pwa",
        message: "PWA installed",
        level: "info",
      });
    }

    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
  };

  const handleUpdate = () => {
    updateServiceWorker(true);
  };

  // Don't render anything on login page
  if (!isAuthenticatedRoute) {
    return null;
  }

  return (
    <>
      {/* Install Prompt */}
      <Modal
        open={showInstallPrompt}
        onCancel={handleDismiss}
        footer={null}
        closeIcon={<CloseOutlined />}
        className="pwa-install-modal"
      >
        <div className="pwa-install-content">
          <img 
            src="/src/favicon.png" 
            alt="ShopEG" 
            className="pwa-install-icon"
          />
          <h3>Install ShopEG</h3>
          <p>
            Install ShopEG on your device for quick access and offline support.
            You'll receive notifications even when the app is closed.
          </p>
          <div className="pwa-install-actions">
            <Button onClick={handleDismiss} className="pwa-dismiss-button">
              Not Now
            </Button>
            <Button type="primary" onClick={handleInstall} className="pwa-install-button">
              Install
            </Button>
          </div>
        </div>
      </Modal>

      {/* Update Prompt */}
      {needRefresh && (
        <div className="pwa-update-banner">
          <span>A new version is available!</span>
          <Button type="primary" size="small" onClick={handleUpdate} className="pwa-update-button">
            Update Now
          </Button>
          <Button size="small" onClick={() => setNeedRefresh(false)} className="pwa-later-button">
            Later
          </Button>
        </div>
      )}
    </>
  );
};

export default PWAInstallPrompt;
